import { Fragment, type CSSProperties } from "react";
import type { InstanceGroupConsolidated } from "@keydials/api-client";
import { bandColour, type Band } from "./groupUi";

/** A small square coloured by band; a dashed hollow box when there's no data for that cell. */
function Cell({ band }: { band: Band }) {
  return (
    <span
      title={band ?? "No data"}
      style={{
        display: "inline-block",
        width: 20,
        height: 20,
        borderRadius: 5,
        background: band ? bandColour(band) : "transparent",
        border: band ? "none" : "var(--border-w) dashed var(--border)",
      }}
    />
  );
}

/**
 * The consolidated "by-area" view: rows are the shared definition's segments (grouped by section), columns are
 * the member dials, each cell coloured by that member's band for the segment, plus an aggregate column.
 */
export function ConsolidatedMatrix({ data }: { data: InstanceGroupConsolidated }) {
  if (!data.uniform) {
    return (
      <div className="kd-card" style={{ padding: 28, textAlign: "center", color: "var(--text-muted)", fontSize: 14, lineHeight: 1.6 }}>
        The by-area view needs every dial in the group to be on the same definition.
      </div>
    );
  }
  if (data.rows.length === 0) {
    return <div style={{ color: "var(--text-muted)", padding: 40, textAlign: "center" }}>Nothing to show yet.</div>;
  }

  // Group consecutive rows by section so each section gets one header row.
  const sections: { key: string; label: string; rows: typeof data.rows }[] = [];
  for (const r of data.rows) {
    const last = sections[sections.length - 1];
    if (last && last.key === r.sectionKey) last.rows.push(r);
    else sections.push({ key: r.sectionKey, label: r.sectionLabel, rows: [r] });
  }

  return (
    <div className="kd-card" style={{ padding: 0, overflowX: "auto" }}>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 13.5 }}>
        <thead>
          <tr>
            <th style={{ ...th, textAlign: "left", minWidth: 180 }}>Area</th>
            {data.members.map((m) => (
              <th key={m.instanceId} style={{ ...th, minWidth: 72 }} title={`${m.name} · ${m.organisationName}`}>
                <span style={{ display: "block", maxWidth: 110, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", margin: "0 auto" }}>{m.name}</span>
              </th>
            ))}
            <th style={{ ...th, minWidth: 72, borderLeft: "var(--border-w) solid var(--border)" }}>Overall</th>
          </tr>
        </thead>
        <tbody>
          {sections.map((sec) => (
            <Fragment key={sec.key}>
              <tr>
                <td colSpan={data.members.length + 2} style={{ padding: "8px 14px", background: "var(--surface-2)", fontSize: 11.5, fontWeight: 800, letterSpacing: "0.04em", textTransform: "uppercase", color: "var(--text-muted)" }}>
                  {sec.label}
                </td>
              </tr>
              {sec.rows.map((r) => (
                <tr key={r.segmentKey}>
                  <td style={{ ...td, textAlign: "left", fontWeight: 600, color: "var(--text)" }}>{r.segmentLabel}</td>
                  {r.cells.map((c) => (
                    <td key={c.instanceId} style={{ ...td, textAlign: "center" }}>
                      <Cell band={c.band} />
                    </td>
                  ))}
                  <td style={{ ...td, textAlign: "center", borderLeft: "var(--border-w) solid var(--border)" }}>
                    <Cell band={r.aggregateBand} />
                  </td>
                </tr>
              ))}
            </Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const th: CSSProperties = { padding: "10px 12px", textAlign: "center", fontSize: 12, fontWeight: 700, color: "var(--text-muted)", borderBottom: "var(--border-w) solid var(--border)" };
const td: CSSProperties = { padding: "9px 12px", borderBottom: "var(--border-w) solid var(--border)" };
